import {normalize} from '../../util/utils';
import {getLeftIds, getRightIds, removeAllForLeft, replaceEdgesForLeft, upsertEdges} from '../../util/relationTable';
import {defaultAuditPlanBodyUi, emptyAuditPlanRelations, getAuditPlanScope} from '../../util/Ares/auditPlanSummaryState';

/**
 * State transitions for the audit plan summary bodies (356 and 357) inside `state.accounts`.
 *
 * These bodies accumulate accounts across pages, because their grid resolves related risks and
 * procedures per row and re-fetching a page the user already visited would drop that work. Every
 * other consumer of `state.accounts` keeps the existing replace-wholesale behaviour, which is why
 * these transitions are reached only from the two action types scoped to these bodies.
 *
 * Two keys are added alongside the normalized accounts:
 *
 * - `relations`: edges between real ids, so a task covering five assertions is stored once
 * - `byBodyId`: what the last response reported for one body, including its ordered `allIds`
 *
 * `allIds` per body is what makes a deleted row disappear: the grid renders that list, so an account
 * removed in the database is absent from the next response and stops being rendered, even though its
 * record stays in the accumulated `byId`.
 *
 * The response reading lives here too, because the shape it walks is the accounts payload of these
 * bodies specifically: risks nested inside assertions, and tasks duplicated across `assertionTasks`
 * and `accountTasks`.
 */

/**
 * Collects a task edge for an account, merging repeat appearances of the same task.
 *
 * A task reached through three assertions is one edge that records all three assertion ids, not
 * three edges. `taskTypeId` and `isRejected` are taken from the first appearance, which is the
 * behaviour the existing body 241 renderers rely on when they de-duplicate by `taskId`.
 * @param {Map} accumulator Map of taskId to pending edge, scoped to one account.
 * @param {Object} task Task row from the response.
 * @param {String} source Which collection the row came from, 'assertion' or 'account'.
 * @returns {void}
 */
function collectTaskEdge(accumulator, task, source) {
	const taskId = task?.taskId;
	if (taskId === null || taskId === undefined) return;

	const existing = accumulator.get(taskId);

	if (!existing) {
		accumulator.set(taskId, {
			rightId: taskId,
			attrs: {
				taskTypeId: task.taskTypeId,
				isRejected: task.isRejected === true,
				sources: [source],
				assertionIds: task.assertionID === null || task.assertionID === undefined ? [] : [task.assertionID]
			}
		});
		return;
	}

	if (!existing.attrs.sources.includes(source)) {
		existing.attrs.sources.push(source);
	}
	if (task.assertionID !== null && task.assertionID !== undefined && !existing.attrs.assertionIds.includes(task.assertionID)) {
		existing.attrs.assertionIds.push(task.assertionID);
	}
}

/**
 * Extracts the relation edges of one account.
 * @param {Object} account Account object from the response.
 * @param {Object} sink Accumulators shared across the whole response.
 * @returns {void}
 */
function collectAccountRelations(account, sink) {
	const accountId = account?.id;
	if (accountId === null || accountId === undefined) return;

	sink.accountIds.push(accountId);

	for (const assertion of account.assertions ?? []) {
		if (!assertion?.id) continue;

		sink.accountAssertionEdges.push({leftId: accountId, rightId: assertion.id, attrs: undefined});

		for (const risk of assertion.risks ?? []) {
			if (!risk?.id) continue;
			sink.assertionRiskEdges.push({leftId: assertion.id, rightId: risk.id, attrs: undefined});
		}
	}

	const taskEdges = new Map();
	for (const task of account.assertionTasks ?? []) {
		collectTaskEdge(taskEdges, task, 'assertion');
	}
	for (const task of account.accountTasks ?? []) {
		collectTaskEdge(taskEdges, task, 'account');
	}

	for (const [taskId, edge] of taskEdges) {
		sink.accountTaskEdges.push({leftId: accountId, rightId: taskId, attrs: edge.attrs});
	}
}

/**
 * Extracts the relation edges and account id order of an audit plan accounts response.
 *
 * The account records themselves are left alone: `accountsReducer` stores them in
 * `state.accounts.byId` as it always has, and assertions and risks are stored by the existing
 * `_assertions` and `_risks` reducers, which read the same response.
 * @param {Array | Object} payload Either the `data` array of a list response or a single account.
 * @returns {Object} The account id order and the edges of each relation.
 */
export function extractAuditPlanRelations(payload) {
	const accounts = Array.isArray(payload) ? payload : [payload];

	const sink = {
		accountIds: [],
		accountAssertionEdges: [],
		assertionRiskEdges: [],
		accountTaskEdges: []
	};

	for (const account of accounts) {
		if (account) collectAccountRelations(account, sink);
	}

	return {
		accountIds: sink.accountIds,
		relations: {
			accountAssertion: sink.accountAssertionEdges,
			assertionRisk: sink.assertionRiskEdges,
			accountTask: sink.accountTaskEdges
		}
	};
}

/**
 * Applies the edges of a response on top of the tables already held.
 * @param {Object} relations Current relation tables.
 * @param {Object} edges Edges produced by `extractAuditPlanRelations`.
 * @returns {Object} Updated relation tables.
 */
function upsertRelations(relations, edges) {
	return {
		accountAssertion: upsertEdges(relations.accountAssertion, edges.accountAssertion),
		assertionRisk: upsertEdges(relations.assertionRisk, edges.assertionRisk),
		accountTask: upsertEdges(relations.accountTask, edges.accountTask)
	};
}

/**
 * Drops the edges of accounts that no body lists any more.
 *
 * An account deleted in the database has to stop contributing risks and procedure counts, so its
 * edges cannot be kept for the rest of the session. Absence from a single response is not enough to
 * conclude that, though: paging away from a row, searching, or the two bodies applying different
 * filters all leave accounts absent from one response while they are still perfectly valid.
 *
 * The test used here is therefore whether any mounted body still lists the account. Nothing reads
 * the edges of an account no body lists, since rows are built from `byBodyId[bodyId].allIds`, and
 * a later response for that account rebuilds its edges. The account record itself stays in `byId`,
 * which is what makes these bodies accumulative.
 *
 * Risk edges hang off the assertion rather than the account, so removing an account's assertion
 * edges would otherwise leave its risks behind under an assertion nothing owns. They are dropped
 * too, but only for an assertion left with no owning account: two accounts can share an assertion,
 * and deleting one of them must not take the other's risks with it.
 * @param {Object} relations Current relation tables.
 * @param {Array} candidateIds Account ids that may have become unreferenced.
 * @param {Object} byBodyId View state of every body, already updated with this response.
 * @returns {Object} Relation tables without the edges of the unreferenced accounts.
 */
function pruneRemovedAccounts(relations, candidateIds, byBodyId) {
	if (!candidateIds.length) return relations;

	const listedAnywhere = new Set();
	for (const bodyUi of Object.values(byBodyId)) {
		for (const accountId of bodyUi.allIds ?? []) listedAnywhere.add(accountId);
	}

	const removed = candidateIds.filter((accountId) => !listedAnywhere.has(accountId));
	if (!removed.length) return relations;

	let accountAssertion = relations.accountAssertion;
	let accountTask = relations.accountTask;
	const orphanCandidates = new Set();
	for (const accountId of removed) {
		for (const assertionId of getRightIds(accountAssertion, accountId)) orphanCandidates.add(assertionId);
		accountAssertion = removeAllForLeft(accountAssertion, accountId);
		accountTask = removeAllForLeft(accountTask, accountId);
	}

	let assertionRisk = relations.assertionRisk;
	for (const assertionId of orphanCandidates) {
		if (!getLeftIds(accountAssertion, assertionId).length) {
			assertionRisk = removeAllForLeft(assertionRisk, assertionId);
		}
	}

	return {...relations, accountAssertion, assertionRisk, accountTask};
}

/**
 * Maps an account, keeping the entity ids already cached when the payload reports none.
 *
 * `mapAccount` rewrites `entities` only when the payload carries them, so a response with
 * `entities: null` would overwrite cached ids with null once merged. That matters here and not for
 * the shared `LIST_ACCOUNTS` case, which replaces the slice wholesale and so has nothing cached to
 * lose, whereas these bodies accumulate: a request made without `includeEntities`, or a drawer fetch
 * for one row, would drop the entity ids an earlier page had already resolved.
 *
 * `GET_ACCOUNT_BY_ID` applies the same fallback. An empty array is deliberately left alone, since it
 * says the account has no entities rather than that none were requested.
 * @param {Object} account Account object from the response.
 * @param {Object} state Current accounts slice.
 * @param {Function} mapAccount Account mapper the accounts reducer already applies.
 * @returns {Object} The mapped account.
 */
function mapWithHeldEntities(account, state, mapAccount) {
	const mapped = mapAccount(account);
	if (mapped?.entities === null) {
		mapped.entities = state.byId?.[account.id]?.entities;
	}
	return mapped;
}

/**
 * Applies a page of audit plan accounts: accumulates the records, upserts the edges and records the
 * row order of the body that asked.
 * @param {Object} state Current accounts slice.
 * @param {Object} action Action carrying the response and the body scope.
 * @param {Function} mapAccount Account mapper the accounts reducer already applies.
 * @returns {Object} Updated accounts slice.
 */
export function applyAuditPlanAccountList(state, action, mapAccount) {
	const scope = getAuditPlanScope(action);
	if (!scope?.bodyId || !action.response?.data) return state;

	const {accountIds, relations: edges} = extractAuditPlanRelations(action.response.data);

	// Accumulative: the response is merged into whatever earlier pages left behind.
	const accumulated = normalize(
		action.response.data.map((account) => mapWithHeldEntities(account, state, mapAccount)),
		undefined,
		state
	);

	const previous = state.byBodyId?.[scope.bodyId] ?? defaultAuditPlanBodyUi;
	const byBodyId = {
		...state.byBodyId,
		[scope.bodyId]: {
			page: action.response.page ?? previous.page,
			pages: action.response.pages ?? 0,
			totalEntityCount: action.response.totalEntityCount ?? 0,
			pageSize: scope.pageSize ?? previous.pageSize,
			allIds: accountIds
		}
	};

	const held = state.relations ?? emptyAuditPlanRelations;
	const relations = pruneRemovedAccounts(upsertRelations(held, edges), previous.allIds, byBodyId);

	return {...accumulated, relations, byBodyId};
}

/**
 * Applies one account fetched for the drawer: merges the record and replaces that account's edges,
 * so relations the response no longer reports are dropped.
 * @param {Object} state Current accounts slice.
 * @param {Object} action Action carrying the response and the body scope.
 * @param {Function} mapAccount Account mapper the accounts reducer already applies.
 * @returns {Object} Updated accounts slice.
 */
export function applyAuditPlanAccountDetail(state, action, mapAccount) {
	const scope = getAuditPlanScope(action);
	const account = action.response;
	if (!scope?.bodyId || !account?.id) return state;

	const {relations: edges} = extractAuditPlanRelations(account);

	const merged = normalize([mapWithHeldEntities(account, state, mapAccount)], undefined, state);
	const held = state.relations ?? emptyAuditPlanRelations;

	// Replacing per assertion needs that assertion's edges together, so they are grouped in one pass
	// rather than re-scanning every edge for each assertion.
	const risksByAssertion = new Map();
	for (const edge of edges.assertionRisk) {
		const group = risksByAssertion.get(edge.leftId);
		if (group) {
			group.push(edge);
		} else {
			risksByAssertion.set(edge.leftId, [edge]);
		}
	}

	// Replacing rather than upserting is what drops stale risks here: an assertion the response still
	// reports gets exactly the risks it now has, and one whose risks are gone is replaced with an
	// empty list. So this path needs no orphan sweep of its own, unlike the list path, which removes
	// whole accounts and has to check whether their assertions kept an owner.
	let assertionRisk = held.assertionRisk;
	for (const edge of edges.accountAssertion) {
		assertionRisk = replaceEdgesForLeft(assertionRisk, edge.rightId, risksByAssertion.get(edge.rightId) ?? []);
	}

	return {
		...merged,
		relations: {
			accountAssertion: replaceEdgesForLeft(held.accountAssertion, account.id, edges.accountAssertion),
			assertionRisk,
			accountTask: replaceEdgesForLeft(held.accountTask, account.id, edges.accountTask)
		}
	};
}
